// Mount the Survey package (#216): build its route from what it needs from this
// app — the initialised i18n instance (it registers its `surveys` namespace
// there), the customer page its tables link to, the signed-in user's
// permissions, a few shared components (employee select, masked field, error
// boundary, active-filter counter) and the error page (as the route's
// errorElement). App.tsx mounts the route from HERE, so the package cannot be
// mounted unregistered. configs/i18n is imported (and so initialised) before the
// registration runs, whatever order main.tsx imports things in. The HTTP client
// goes through core's registry (utils/axios/register-core.ts).
import {
    createSurveyModule,
    type SurveyHost,
} from '@vnetwork/datacentral-survey'
import i18n from '@/configs/i18n'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import ErrorBoundary from '@/components/ErrorBoundary'
import PrivacyField from '@/components/PrivacyField'
import { useActiveFilterCount } from '@/hooks/useActiveFilterCount'
import usePermissionsStore from '@/stores/usePermissionsStore'
import { lazy, Suspense } from 'react'
import { paths } from '@/utils/path'

// Lazy: the select only renders in the filters of the report and of the create
// page. Imported eagerly here it would pull its loaders into the entry chunk;
// before the move it loaded with the Survey route chunk (#212).
const LazyEmployeeSelect = lazy(
    () => import('@/components/Form/Shared/EmployeeSelect')
)
const EmployeeSelect: SurveyHost['EmployeeSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyEmployeeSelect {...props} />
    </Suspense>
)

const LazyCustomerSelect = lazy(
    () => import('@/components/Form/Shared/CustomerSelect')
)
const CustomerSelect: SurveyHost['CustomerSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyCustomerSelect {...props} />
    </Suspense>
)

const usePermissions = () => {
    const { permissions, isAdmin } = usePermissionsStore()
    return { permissions, isAdmin }
}

// The package does not render the app's ErrorPage; the host adds it as the
// route's errorElement, as for Warehouse (#59), Dayoffs (#204) and Business.
export const SurveyRoute = {
    ...createSurveyModule({
        i18n,
        // Before the move this was `paths.customerDetail` of the app-wide
        // `paths` inside the tables' links: it is named here now, so a renamed
        // route fails typecheck in this file.
        links: {
            customerDetail: paths.customerDetail,
            ticketDetail: paths.ticketDetail,
        },
        usePermissions,
        EmployeeSelect,
        CustomerSelect,
        PrivacyField,
        ErrorBoundary,
        useActiveFilterCount,
    }),
    errorElement: <ErrorPage status={500} />,
}
